import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaArrowUp, FaArrowDown } from 'react-icons/fa';

const HomePageManager = () => {
  const [sections, setSections] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    type: 'hero',
    title: '',
    subtitle: '',
    description: '',
    linkUrl: '',
    buttonText: '',
    buttonLink: '',
    orderIndex: 0,
    isActive: true,
    extraData: ''
  });
  const [imageFiles, setImageFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activityCards, setActivityCards] = useState([]);

  const sectionTypes = [
    { value: 'hero', label: 'Hero (Karşılama)' },
    { value: 'features', label: 'Özellikler' },
    { value: 'cta', label: 'CTA (Çağrı)' },
    { value: 'content', label: 'İçerik' },
    { value: 'image-text', label: 'Görsel + Metin' },
    { value: 'testimonials', label: 'Referanslar' },
    { value: 'activity-cards', label: 'Etkinlik Kartları' },
    { value: 'custom', label: 'Özel Bölüm' }
  ];

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/homepage/admin', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSections(response.data);
    } catch (error) {
      console.error('Error fetching sections:', error);
      alert('Bölümler yüklenirken bir hata oluştu');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      const formDataToSend = new FormData();

      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          formDataToSend.append(key, formData[key]);
        }
      });

      // For activity-cards, save cards array to extraData
      if (formData.type === 'activity-cards') {
        const cardsData = JSON.stringify({ cards: activityCards });
        formDataToSend.set('extraData', cardsData);
      }

      // Append existing images
      if (existingImages.length > 0) {
        formDataToSend.append('images', JSON.stringify(existingImages));
      }

      // Append new image files
      imageFiles.forEach(file => {
        formDataToSend.append('images', file);
      });

      if (editingItem) {
        await axios.put(`/api/homepage/${editingItem.id}`, formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        await axios.post('/api/homepage', formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      alert(editingItem ? 'Bölüm güncellendi' : 'Bölüm eklendi');
      setShowForm(false);
      setEditingItem(null);
      setFormData({
        type: 'hero',
        title: '',
        subtitle: '',
        description: '',
        linkUrl: '',
        buttonText: '',
        buttonLink: '',
        orderIndex: 0,
        isActive: true,
        extraData: ''
      });
      setImageFiles([]);
      setExistingImages([]);
      setActivityCards([]);
      fetchSections();
    } catch (error) {
      console.error('Error saving section:', error);
      alert(error.response?.data?.error || 'Bölüm kaydedilirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (section) => {
    setEditingItem(section);
    setFormData({
      type: section.type || 'hero',
      title: section.title || '',
      subtitle: section.subtitle || '',
      description: section.description || '',
      linkUrl: section.linkUrl || section.link_url || '',
      buttonText: section.buttonText || section.button_text || '',
      buttonLink: section.buttonLink || section.button_link || '',
      orderIndex: section.orderIndex || section.order_index || 0,
      isActive: section.isActive !== undefined ? section.isActive : (section.is_active === 1 || section.is_active === true),
      extraData: section.extraData || section.extra_data || ''
    });
    
    // Parse existing images
    let parsedImages = [];
    if (section.images) {
      try {
        parsedImages = JSON.parse(section.images);
      } catch (e) {
        // If not JSON, might be single imageUrl
        if (section.imageUrl || section.image_url) {
          parsedImages = [section.imageUrl || section.image_url];
        }
      }
    } else if (section.imageUrl || section.image_url) {
      parsedImages = [section.imageUrl || section.image_url];
    }
    setExistingImages(parsedImages);
    setImageFiles([]);
    
    // Parse activity cards if type is activity-cards
    if (section.type === 'activity-cards') {
      try {
        const extraData = section.extraData || section.extra_data || '{}';
        const parsed = JSON.parse(extraData);
        if (parsed.cards && Array.isArray(parsed.cards)) {
          setActivityCards(parsed.cards);
        } else {
          setActivityCards([]);
        }
      } catch (e) {
        setActivityCards([]);
      }
    } else {
      setActivityCards([]);
    }
    
    setShowForm(true);
  };

  const handleRemoveImage = (index) => {
    setExistingImages(existingImages.filter((_, i) => i !== index));
  };

  const handleRemoveNewImage = (index) => {
    setImageFiles(imageFiles.filter((_, i) => i !== index));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu bölümü silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`/api/homepage/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Bölüm silindi');
      fetchSections();
    } catch (error) {
      console.error('Error deleting section:', error);
      alert('Bölüm silinirken bir hata oluştu');
    }
  };

  const handleToggleActive = async (section) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`/api/homepage/${section.id}`, {
        ...formData,
        type: section.type || section.type,
        title: section.title || '',
        subtitle: section.subtitle || '',
        description: section.description || '',
        linkUrl: section.linkUrl || section.link_url || '',
        buttonText: section.buttonText || section.button_text || '',
        buttonLink: section.buttonLink || section.button_link || '',
        orderIndex: section.orderIndex || section.order_index || 0,
        isActive: !(section.isActive !== undefined ? section.isActive : (section.is_active === 1 || section.is_active === true)),
        imageUrl: section.imageUrl || section.image_url || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchSections();
    } catch (error) {
      console.error('Error toggling active:', error);
      alert('Durum güncellenirken bir hata oluştu');
    }
  };

  const handleMove = async (id, direction) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`/api/homepage/${id}/move`, { direction }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchSections();
    } catch (error) {
      console.error('Error moving section:', error);
      alert('Bölüm taşınırken bir hata oluştu');
    }
  };

  return (
    <div className="bg-gradient-card rounded-xl p-6 border border-gold/30">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gold">Ana Sayfa Yönetimi</h2>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingItem(null);
            setFormData({
              type: 'hero',
              title: '',
              subtitle: '',
              description: '',
              linkUrl: '',
              buttonText: '',
              buttonLink: '',
              orderIndex: sections.length,
              isActive: true,
              extraData: ''
            });
            setImageFiles([]);
            setExistingImages([]);
            setActivityCards([]);
          }}
          className="bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-6 py-2 rounded-lg transition-all duration-300 shadow-gold"
        >
          + Yeni Bölüm Ekle
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="mb-6 p-6 bg-navy-800 rounded-lg border border-gold/20">
          <h3 className="text-xl font-bold text-gold mb-4">
            {editingItem ? 'Bölüm Düzenle' : 'Yeni Bölüm Ekle'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2">Bölüm Tipi *</label>
                <select
                  value={formData.type}
                  onChange={(e) => {
                    setFormData({ ...formData, type: e.target.value });
                    if (e.target.value !== 'activity-cards') {
                      setActivityCards([]);
                    }
                  }}
                  className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                  required
                >
                  {sectionTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-white mb-2">Sıra</label>
                <input
                  type="number"
                  value={formData.orderIndex}
                  onChange={(e) => setFormData({ ...formData, orderIndex: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                />
              </div>
            </div>

            <div>
              <label className="block text-white mb-2">Başlık</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                placeholder="Bölüm başlığı"
              />
            </div>

            <div>
              <label className="block text-white mb-2">Alt Başlık</label>
              <textarea
                value={formData.subtitle}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                rows="2"
                placeholder="Alt başlık veya kısa açıklama"
              />
            </div>

            <div>
              <label className="block text-white mb-2">Açıklama</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                rows="4"
                placeholder="Detaylı açıklama"
              />
            </div>

            <div>
              <label className="block text-white mb-2">Görseller (Birden fazla seçebilirsiniz)</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  setImageFiles([...imageFiles, ...files]);
                }}
                className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
              />
              
              {/* Existing Images */}
              {existingImages.length > 0 && (
                <div className="mt-4">
                  <p className="text-white/80 mb-2 text-sm">Mevcut Görseller:</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {existingImages.map((img, index) => (
                      <div key={index} className="relative group">
                        <img src={img} alt={`Existing ${index + 1}`} className="w-full h-24 object-cover rounded border border-gold/30" />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New Images Preview */}
              {imageFiles.length > 0 && (
                <div className="mt-4">
                  <p className="text-white/80 mb-2 text-sm">Yeni Görseller:</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {imageFiles.map((file, index) => (
                      <div key={index} className="relative group">
                        <img src={URL.createObjectURL(file)} alt={`New ${index + 1}`} className="w-full h-24 object-cover rounded border border-gold/30" />
                        <button
                          type="button"
                          onClick={() => handleRemoveNewImage(index)}
                          className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2">Link URL</label>
                <input
                  type="url"
                  value={formData.linkUrl}
                  onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
                  className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                  placeholder="https://..."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2">Buton Metni</label>
                <input
                  type="text"
                  value={formData.buttonText}
                  onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })}
                  className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                  placeholder="Örn: Hemen Üye Ol"
                />
              </div>

              <div>
                <label className="block text-white mb-2">Buton Link</label>
                <input
                  type="url"
                  value={formData.buttonLink}
                  onChange={(e) => setFormData({ ...formData, buttonLink: e.target.value })}
                  className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                  placeholder="/register veya https://..."
                />
              </div>
            </div>

            {/* Activity Cards Management */}
            {formData.type === 'activity-cards' && (
              <div className="border-t border-gold/30 pt-6 mt-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-bold text-gold">Etkinlik Kartları</h4>
                  <button
                    type="button"
                    onClick={() => {
                      const newCard = {
                        title: '',
                        description: '',
                        buttonText: '',
                        buttonLink: '',
                        gradientFrom: 'from-purple-500',
                        gradientTo: 'to-blue-500',
                        iconImage: '',
                        orderIndex: activityCards.length
                      };
                      setActivityCards([...activityCards, newCard]);
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm"
                  >
                    + Kart Ekle
                  </button>
                </div>

                <div className="space-y-4">
                  {activityCards.map((card, index) => (
                    <div key={index} className="bg-navy-900 rounded-lg p-4 border border-gold/20">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-white font-semibold">Kart {index + 1}</span>
                        <div className="flex gap-2">
                          {index > 0 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newCards = [...activityCards];
                                [newCards[index], newCards[index - 1]] = [newCards[index - 1], newCards[index]];
                                setActivityCards(newCards);
                              }}
                              className="text-white/60 hover:text-white"
                            >
                              ↑
                            </button>
                          )}
                          {index < activityCards.length - 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newCards = [...activityCards];
                                [newCards[index], newCards[index + 1]] = [newCards[index + 1], newCards[index]];
                                setActivityCards(newCards);
                              }}
                              className="text-white/60 hover:text-white"
                            >
                              ↓
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setActivityCards(activityCards.filter((_, i) => i !== index));
                            }}
                            className="text-red-400 hover:text-red-300"
                          >
                            Sil
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-white/80 mb-1 text-sm">Başlık</label>
                          <input
                            type="text"
                            value={card.title}
                            onChange={(e) => {
                              const newCards = [...activityCards];
                              newCards[index].title = e.target.value;
                              setActivityCards(newCards);
                            }}
                            className="w-full px-3 py-2 bg-navy-800 border border-gold/30 rounded text-white text-sm"
                            placeholder="Kart başlığı"
                          />
                        </div>

                        <div>
                          <label className="block text-white/80 mb-1 text-sm">Açıklama</label>
                          <input
                            type="text"
                            value={card.description}
                            onChange={(e) => {
                              const newCards = [...activityCards];
                              newCards[index].description = e.target.value;
                              setActivityCards(newCards);
                            }}
                            className="w-full px-3 py-2 bg-navy-800 border border-gold/30 rounded text-white text-sm"
                            placeholder="Kart açıklaması"
                          />
                        </div>

                        <div>
                          <label className="block text-white/80 mb-1 text-sm">Buton Metni</label>
                          <input
                            type="text"
                            value={card.buttonText}
                            onChange={(e) => {
                              const newCards = [...activityCards];
                              newCards[index].buttonText = e.target.value;
                              setActivityCards(newCards);
                            }}
                            className="w-full px-3 py-2 bg-navy-800 border border-gold/30 rounded text-white text-sm"
                            placeholder="Buton metni"
                          />
                        </div>

                        <div>
                          <label className="block text-white/80 mb-1 text-sm">Buton Link</label>
                          <input
                            type="text"
                            value={card.buttonLink}
                            onChange={(e) => {
                              const newCards = [...activityCards];
                              newCards[index].buttonLink = e.target.value;
                              setActivityCards(newCards);
                            }}
                            className="w-full px-3 py-2 bg-navy-800 border border-gold/30 rounded text-white text-sm"
                            placeholder="/path veya https://..."
                          />
                        </div>

                        <div>
                          <label className="block text-white/80 mb-1 text-sm">Gradient Başlangıç Rengi</label>
                          <select
                            value={card.gradientFrom}
                            onChange={(e) => {
                              const newCards = [...activityCards];
                              newCards[index].gradientFrom = e.target.value;
                              setActivityCards(newCards);
                            }}
                            className="w-full px-3 py-2 bg-navy-800 border border-gold/30 rounded text-white text-sm"
                          >
                            <option value="from-purple-500">Mor</option>
                            <option value="from-blue-500">Mavi</option>
                            <option value="from-green-500">Yeşil</option>
                            <option value="from-yellow-500">Sarı</option>
                            <option value="from-red-500">Kırmızı</option>
                            <option value="from-pink-500">Pembe</option>
                            <option value="from-indigo-500">İndigo</option>
                            <option value="from-teal-500">Teal</option>
                            <option value="from-cyan-500">Cyan</option>
                            <option value="from-orange-500">Turuncu</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-white/80 mb-1 text-sm">Gradient Bitiş Rengi</label>
                          <select
                            value={card.gradientTo}
                            onChange={(e) => {
                              const newCards = [...activityCards];
                              newCards[index].gradientTo = e.target.value;
                              setActivityCards(newCards);
                            }}
                            className="w-full px-3 py-2 bg-navy-800 border border-gold/30 rounded text-white text-sm"
                          >
                            <option value="to-blue-500">Mavi</option>
                            <option value="to-purple-500">Mor</option>
                            <option value="to-green-500">Yeşil</option>
                            <option value="to-yellow-500">Sarı</option>
                            <option value="to-red-500">Kırmızı</option>
                            <option value="to-pink-500">Pembe</option>
                            <option value="to-indigo-500">İndigo</option>
                            <option value="to-teal-500">Teal</option>
                            <option value="to-cyan-500">Cyan</option>
                            <option value="to-orange-500">Turuncu</option>
                          </select>
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-white/80 mb-1 text-sm">İkon Görseli</label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                try {
                                  const token = localStorage.getItem('adminToken');
                                  const formDataToSend = new FormData();
                                  formDataToSend.append('image', file);
                                  
                                  const response = await axios.post('/api/homepage/upload-icon', formDataToSend, {
                                    headers: {
                                      Authorization: `Bearer ${token}`,
                                      'Content-Type': 'multipart/form-data'
                                    }
                                  });
                                  
                                  const newCards = [...activityCards];
                                  newCards[index].iconImage = response.data.url;
                                  setActivityCards(newCards);
                                } catch (error) {
                                  console.error('Error uploading icon:', error);
                                  alert('İkon yüklenirken bir hata oluştu');
                                }
                              }
                            }}
                            className="w-full px-3 py-2 bg-navy-800 border border-gold/30 rounded text-white text-sm"
                          />
                          {card.iconImage && (
                            <div className="mt-2">
                              <img src={card.iconImage} alt="Icon" className="w-16 h-16 object-contain rounded bg-navy-900 p-2" />
                              <button
                                type="button"
                                onClick={() => {
                                  const newCards = [...activityCards];
                                  newCards[index].iconImage = '';
                                  setActivityCards(newCards);
                                }}
                                className="mt-2 text-red-400 hover:text-red-300 text-sm"
                              >
                                İkonu Kaldır
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {formData.type !== 'activity-cards' && (
              <div>
                <label className="block text-white mb-2">Ekstra Veri (JSON - Opsiyonel)</label>
                <textarea
                  value={formData.extraData}
                  onChange={(e) => setFormData({ ...formData, extraData: e.target.value })}
                  className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold font-mono text-sm"
                  rows="3"
                  placeholder='{"key": "value"}'
                />
              </div>
            )}

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-white">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4"
                />
                Aktif
              </label>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={loading}
                className="bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-6 py-2 rounded-lg transition-all duration-300 shadow-gold disabled:opacity-50"
              >
                {loading ? 'Kaydediliyor...' : (editingItem ? 'Güncelle' : 'Ekle')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingItem(null);
                  setImageFiles([]);
                  setExistingImages([]);
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors"
              >
                İptal
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Sections List */}
      <div className="space-y-4">
        {sections.map((section) => (
          <div
            key={section.id}
            className={`p-4 rounded-lg border-2 ${
              section.isActive !== undefined ? section.isActive : (section.is_active === 1 || section.is_active === true)
                ? 'bg-navy-800 border-gold/30'
                : 'bg-navy-900/50 border-gray-600/30 opacity-60'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-3 py-1 bg-gold/20 text-gold rounded text-sm font-semibold">
                    {sectionTypes.find(t => t.value === section.type)?.label || section.type}
                  </span>
                  <span className="text-white/60 text-sm">Sıra: {section.orderIndex || section.order_index || 0}</span>
                  {(section.isActive !== undefined ? section.isActive : (section.is_active === 1 || section.is_active === true)) ? (
                    <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">Aktif</span>
                  ) : (
                    <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">Pasif</span>
                  )}
                </div>
                {section.title && (
                  <h3 className="text-white font-bold text-lg mb-1">{section.title}</h3>
                )}
                {section.subtitle && (
                  <p className="text-white/80 mb-2">{section.subtitle}</p>
                )}
                {(() => {
                  let images = [];
                  if (section.images) {
                    try {
                      images = JSON.parse(section.images);
                    } catch (e) {
                      if (section.imageUrl || section.image_url) {
                        images = [section.imageUrl || section.image_url];
                      }
                    }
                  } else if (section.imageUrl || section.image_url) {
                    images = [section.imageUrl || section.image_url];
                  }
                  
                  return images.length > 0 ? (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {images.map((img, index) => (
                        <img
                          key={index}
                          src={img}
                          alt={`${section.title || 'Section'} ${index + 1}`}
                          className="h-20 object-contain rounded border border-gold/30"
                        />
                      ))}
                    </div>
                  ) : null;
                })()}
              </div>
              <div className="flex flex-col gap-2 ml-4">
                <div className="flex gap-2">
                  <button
                    onClick={() => handleMove(section.id, 'up')}
                    disabled={sections.findIndex(s => s.id === section.id) === 0}
                    className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Yukarı Taşı"
                  >
                    <FaArrowUp />
                  </button>
                  <button
                    onClick={() => handleMove(section.id, 'down')}
                    disabled={sections.findIndex(s => s.id === section.id) === sections.length - 1}
                    className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Aşağı Taşı"
                  >
                    <FaArrowDown />
                  </button>
                </div>
                <button
                  onClick={() => handleEdit(section)}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                >
                  Düzenle
                </button>
                <button
                  onClick={() => handleToggleActive(section)}
                  className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                    section.isActive !== undefined ? section.isActive : (section.is_active === 1 || section.is_active === true)
                      ? 'bg-yellow-500 hover:bg-yellow-600 text-navy-900'
                      : 'bg-green-500 hover:bg-green-600 text-white'
                  }`}
                >
                  {(section.isActive !== undefined ? section.isActive : (section.is_active === 1 || section.is_active === true)) ? 'Pasif Yap' : 'Aktif Yap'}
                </button>
                <button
                  onClick={() => handleDelete(section.id)}
                  className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                >
                  Sil
                </button>
              </div>
            </div>
          </div>
        ))}

        {sections.length === 0 && (
          <div className="text-center text-white/60 py-8">
            <p>Henüz bölüm eklenmedi</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePageManager;
